import type React from "react";
import { useState } from "react";
import type { Goal } from "@/types/goal";

type GoalFormData = Omit<Goal, "id" | "userId" | "createdAt" | "updatedAt">;

interface GoalFormProps {
	initialData?: GoalFormData;
	onSubmit: (formData: GoalFormData) => Promise<void>;
}

const GoalForm: React.FC<GoalFormProps> = ({ initialData, onSubmit }) => {
	const [formData, setFormData] = useState<GoalFormData>(
		initialData || {
			title: "",
			description: "",
			category: "short",
			startDate: "",
			endDate: "",
			priority: "medium",
			status: "not_started",
		},
	);

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
		>,
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await onSubmit(formData);
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div>
				<label htmlFor="title" className="block mb-1">
					タイトル
				</label>
				<input
					type="text"
					id="title"
					name="title"
					value={formData.title}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					required
				/>
			</div>
			<div>
				<label htmlFor="description" className="block mb-1">
					説明
				</label>
				<textarea
					id="description"
					name="description"
					value={formData.description || ""}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					rows={4}
				/>
			</div>
			<div>
				<label htmlFor="category" className="block mb-1">
					カテゴリー
				</label>
				<select
					id="category"
					name="category"
					value={formData.category}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					required
				>
					<option value="short">短期</option>
					<option value="medium">中期</option>
					<option value="long">長期</option>
				</select>
			</div>
			<div>
				<label htmlFor="startDate" className="block mb-1">
					開始日
				</label>
				<input
					type="date"
					id="startDate"
					name="startDate"
					value={formData.startDate}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					required
				/>
			</div>
			<div>
				<label htmlFor="endDate" className="block mb-1">
					終了日
				</label>
				<input
					type="date"
					id="endDate"
					name="endDate"
					value={formData.endDate}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					required
				/>
			</div>
			<div>
				<label htmlFor="priority" className="block mb-1">
					優先度
				</label>
				<select
					id="priority"
					name="priority"
					value={formData.priority}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					required
				>
					<option value="low">低</option>
					<option value="medium">中</option>
					<option value="high">高</option>
				</select>
			</div>
			<div>
				<label htmlFor="status" className="block mb-1">
					ステータス
				</label>
				<select
					id="status"
					name="status"
					value={formData.status}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded"
					required
				>
					<option value="not_started">未着手</option>
					<option value="in_progress">進行中</option>
					<option value="completed">完了</option>
				</select>
			</div>
			<button
				type="submit"
				className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
			>
				{initialData ? "更新" : "作成"}
			</button>
		</form>
	);
};

export default GoalForm;
